// Debounces input on a search box and submits a Turbo Stream GET to the
// configured URL. The response is expected to update a target DOM id with
// new search results. Used by the Provider Intelligence "Add provider"
// drawer (see app/views/partners/provider_intelligence/add.html.erb).
//
// Mount on the `<input>` itself. Optional `busy-class` value is a CSS class
// toggled on the input while a request is in flight (e.g. a shimmer/loading
// background defined in application.tailwind.css).
//
// Example:
//   <input data-controller="auto-search"
//          data-auto-search-url-value="/partners/foo/provider_intelligence/search"
//          data-auto-search-wait-value="300"
//          data-auto-search-busy-class-value="add-provider-search-busy">
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    url: String,
    wait: { type: Number, default: 300 },
    statusId: String,
    resultsId: String,
    busyClass: { type: String, default: "" },
    eventName: { type: String, default: "add-provider:results-updated" },
  };

  connect() {
    this.timeout = null;
    this.abortController = null;
    this.requestId = 0;
    this._boundOnInput = this.onInput.bind(this);
    this.element.addEventListener("input", this._boundOnInput);
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout);
    if (this.abortController) this.abortController.abort();
    this.element.removeEventListener("input", this._boundOnInput);
  }

  onInput() {
    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.fetchResults(), this.waitValue);
  }

  async fetchResults() {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    const requestId = ++this.requestId;

    this.setBusy(true);
    this.setStatus("Searching...");

    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set("q", this.element.value);
    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: "text/vnd.turbo-stream.html" },
        signal: this.abortController.signal,
      });
      if (response.ok) {
        const text = await response.text();
        // Drop the response if a newer request has started while we were
        // reading the body. Applying stale Turbo Stream HTML would briefly
        // show out-of-date results before the newer request lands.
        if (requestId !== this.requestId) return;
        window.Turbo.renderStreamMessage(text);
        this.setStatus("Search complete");
        requestAnimationFrame(() => {
          if (requestId !== this.requestId) return;
          window.dispatchEvent(new CustomEvent(this.eventNameValue));
        });
      } else if (requestId === this.requestId) {
        this.setStatus("Search failed, try again");
      }
    } catch (error) {
      if (error.name !== "AbortError" && requestId === this.requestId) {
        this.setStatus("Search failed, try again");
      }
    } finally {
      if (requestId === this.requestId) this.setBusy(false);
    }
  }

  setStatus(message) {
    if (!this.hasStatusIdValue) return;
    const el = document.getElementById(this.statusIdValue);
    if (!el) return;

    // Busy state is communicated visually via the busy-class shimmer; never
    // print "Searching..." / "Search complete" copy. The status line is for
    // errors only.
    if (message === "Searching..." || message === "Search complete") {
      el.textContent = "";
      el.classList.add("hidden");
      return;
    }

    el.textContent = message;
    el.classList.remove("hidden");
  }

  setBusy(isBusy) {
    if (this.busyClassValue) {
      this.element.classList.toggle(this.busyClassValue, isBusy);
    }
    this.element.setAttribute("aria-busy", isBusy ? "true" : "false");

    if (!this.hasResultsIdValue) return;
    const el = document.getElementById(this.resultsIdValue);
    if (el) el.setAttribute("aria-busy", isBusy ? "true" : "false");
  }
}
